import React from 'react'
import {
  Box,
  Badge,
  Button,
  Card,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Flex
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { FiArrowRight, FiCheck, FiUsers, FiTrendingUp, FiBarChart2, FiZap, FiGlobe, FiTarget, FiX } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'
import { EstimatesTable } from '../components/Spotlight/EstimatesTable'

export const ForPractitionersPage: React.FC = () => {
  const navigate = useNavigate()

  const handleGetStarted = () => navigate('/for-services/get-started')
  const handleHowItWorks = () => navigate('/how-it-works')

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column">
      <SpotlightHeader />

      <Box flex={1}>
        {/* Hero */}
        <Box
          bgGradient="linear(to-br, white, blue.50, brand.50)"
          pt={{ base: 20, md: 32 }}
          pb={{ base: 16, md: 24 }}
          position="relative"
          overflow="hidden"
        >
          {/* Background decoration */}
          <Box
            position="absolute"
            top="-10%"
            right="-5%"
            w="800px"
            h="800px"
            borderRadius="full"
            bgGradient="radial(brand.100, transparent)"
            opacity={0.4}
            filter="blur(80px)"
          />
          <Box
            position="absolute"
            bottom="-10%"
            left="-5%"
            w="600px"
            h="600px"
            borderRadius="full"
            bgGradient="radial(blue.100, transparent)"
            opacity={0.4}
            filter="blur(80px)"
          />

          <Container maxW="7xl" position="relative" zIndex={1}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={16} alignItems="center">
              <VStack align="start" gap={8} textAlign="left">
                <Badge 
                  colorPalette="brand" 
                  size="lg" 
                  px={4} 
                  py={2} 
                  borderRadius="full"
                  variant="subtle"
                  border="1px solid"
                  borderColor="brand.200"
                >
                  For Services
                </Badge>
                
                <VStack align="start" gap={6}>
                  <Heading
                    size={{ base: '3xl', md: '4xl', lg: '5xl' }}
                    color="gray.900"
                    lineHeight="1.1"
                    fontWeight="extrabold"
                    letterSpacing="tight"
                  >
                    Get clients without learning marketing.
                  </Heading>

                  <Text
                    fontSize={{ base: 'xl', md: '2xl' }}
                    color="gray.600"
                    lineHeight="tall"
                    maxW="xl"
                  >
                    We handle ads, platforms, and optimization. You never touch an ad manager. No marketing courses, no ad managers to learn—just results.
                  </Text>

                  <Text fontSize="md" color="gray.500" fontStyle="italic">
                    "For transformation-focused service providers who deliver real change."
                  </Text>
                </VStack>

                <HStack gap={4} flexWrap="wrap" pt={2}>
                  <Button
                    colorPalette="brand"
                    size="xl"
                    fontWeight="bold"
                    px={10}
                    h={16}
                    fontSize="lg"
                    onClick={handleGetStarted}
                    _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                    transition="all 0.2s"
                  >
                    <HStack gap={3}>
                      <Text>Start Getting Clients</Text>
                      <Icon as={FiArrowRight} />
                    </HStack>
                  </Button>
                  <Button
                    variant="ghost"
                    size="xl"
                    fontWeight="semibold"
                    px={8}
                    h={16}
                    fontSize="lg"
                    onClick={handleHowItWorks}
                    color="gray.600"
                    _hover={{ bg: 'gray.50', color: 'brand.700' }}
                  >
                    See How It Works
                  </Button>
                </HStack>

                {/* Social Proof / Trust */}
                <HStack gap={8} pt={8} borderTop="1px solid" borderColor="gray.100" w="full">
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">100%</Text>
                    <Text fontSize="sm" color="gray.500">Managed For You</Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">0</Text>
                    <Text fontSize="sm" color="gray.500">Tech Skills Needed</Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">Pay</Text>
                    <Text fontSize="sm" color="gray.500">As You Grow</Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">24/7</Text>
                    <Text fontSize="sm" color="gray.500">Auto-Promotion</Text>
                  </VStack>
                </HStack>
              </VStack>

              {/* Hero Illustration */}
              <Box 
                position="relative"
                display={{ base: 'none', lg: 'block' }}
              >
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="120%"
                  h="120%"
                  bgGradient="radial(brand.500, transparent 70%)"
                  opacity={0.1}
                  filter="blur(60px)"
                />
                
                {/* Main Card - Spotlight Page Mockup */}
                <Box
                  bg="white"
                  borderRadius="2xl"
                  shadow="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  position="relative"
                  overflow="hidden"
                  transform="perspective(1000px) rotateY(-5deg) rotateX(2deg)"
                  transition="transform 0.5s"
                  _hover={{ transform: 'perspective(1000px) rotateY(0deg) rotateX(0deg)' }}
                >
                  {/* Mock Browser Header */}
                  <HStack bg="gray.50" px={4} py={3} borderBottom="1px solid" borderColor="gray.100" gap={2}>
                    <Box w={3} h={3} borderRadius="full" bg="red.300" />
                    <Box w={3} h={3} borderRadius="full" bg="yellow.300" />
                    <Box w={3} h={3} borderRadius="full" bg="green.300" />
                    <Box flex={1} mx={4} bg="white" borderRadius="md" h={6} shadow="xs" />
                  </HStack>
                  
                  {/* Mock Content */}
                  <Box p={8}>
                    <HStack align="start" gap={6} mb={8}>
                      <Box w={24} h={24} borderRadius="full" bg="gray.200" flexShrink={0} />
                      <VStack align="start" flex={1} gap={2}>
                        <Box w="80%" h={6} bg="gray.800" borderRadius="md" />
                        <Box w="40%" h={4} bg="brand.500" borderRadius="md" />
                        <Box w="100%" h={3} bg="gray.100" borderRadius="md" mt={2} />
                        <Box w="100%" h={3} bg="gray.100" borderRadius="md" />
                      </VStack>
                    </HStack>
                    <HStack gap={4} mb={10}>
                      <Box flex={1} h={24} bg="brand.50" borderRadius="lg" border="1px dashed" borderColor="brand.200" />
                      <Box flex={1} h={24} bg="blue.50" borderRadius="lg" border="1px dashed" borderColor="blue.200" />
                    </HStack>
                    <Button w="full" colorPalette="brand" size="lg">Book Consultation</Button>
                  </Box>

                  {/* Floating Analytics Card */}
                  <Box
                    position="absolute"
                    bottom={8}
                    right={-8}
                    bg="white"
                    p={4}
                    borderRadius="xl"
                    shadow="xl"
                    border="1px solid"
                    borderColor="gray.100"
                    w="200px"
                    transform="translateZ(20px)"
                  >
                    <VStack align="start" gap={2}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Weekly Views</Text>
                      <HStack align="end" justify="space-between" w="full">
                        <Text fontSize="2xl" fontWeight="bold" color="gray.900">1,248</Text>
                        <Badge colorPalette="green" variant="solid">+12%</Badge>
                      </HStack>
                      <Box w="full" h={1} bg="gray.100" borderRadius="full" overflow="hidden">
                        <Box w="70%" h="full" bg="green.500" />
                      </Box>
                    </VStack>
                  </Box>
                </Box>
              </Box>
            </SimpleGrid>
          </Container>
        </Box>

        {/* How Spotlight Works - 5 Steps */}
        <Container maxW="7xl" py={{ base: 16, md: 24 }}>
          <VStack align="stretch" gap={16}>
            <Box textAlign="center" maxW="3xl" mx="auto">
              <Badge colorPalette="blue" size="lg" mb={4} variant="subtle">
                The Process
              </Badge>
              <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" mb={6} letterSpacing="tight">
                How Spotlight Works
              </Heading>
              <Text fontSize="xl" color="gray.600">
                A simple, automated system that turns "marketing" into a background task.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3, lg: 5 }} gap={8}>
              {[
                {
                  step: 1,
                  title: "Start in minutes",
                  description: "Create your account and complete a brief questionnaire about your services."
                },
                {
                  step: 2,
                  title: "We generate your carousel instantly",
                  description: "We generate your carousel ads (4-7 slides) from your answers. Preview immediately and optionally refine story beats."
                },
                {
                  step: 3,
                  title: "You preview and refine your carousel",
                  description: "See 3 carousel variations, optionally add story details (before moment, turning point, approach, expectations)."
                },
                {
                  step: 4,
                  title: "You choose a visibility pack",
                  description: "Select an impression-based pack that fits your goals. Pay only when you activate a pack."
                },
                {
                  step: 5,
                  title: "Ads run automatically on Meta",
                  description: "Carousel ads run automatically on Meta. Track simple analytics and respond to leads as they come in."
                }
              ].map((item, idx) => (
                <Box 
                  key={item.step}
                  position="relative"
                >
                  {/* Connector Line */}
                  {idx < 4 && (
                    <Box 
                      display={{ base: 'none', lg: 'block' }}
                      position="absolute" 
                      top="24px" 
                      right="-50%" 
                      w="100%" 
                      h="2px" 
                      bg="gray.100" 
                      zIndex={0}
                    />
                  )}
                  
                  <VStack align="start" gap={4} position="relative" zIndex={1}>
                    <Flex 
                      w={12} 
                      h={12} 
                      borderRadius="full" 
                      bg={idx === 4 ? "brand.500" : "white"}
                      border="2px solid"
                      borderColor={idx === 4 ? "brand.500" : "brand.200"}
                      align="center" 
                      justify="center"
                      color={idx === 4 ? "white" : "brand.600"}
                      fontSize="xl"
                      fontWeight="bold"
                      shadow="md"
                    >
                      {item.step}
                    </Flex>
                    <Heading size="md" color="gray.900" fontWeight="bold" lineHeight="short">
                      {item.title}
                    </Heading>
                    <Text fontSize="md" color="gray.600" lineHeight="tall">
                      {item.description}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>

            <Flex justify="center" pt={8}>
              <Button
                size="lg"
                colorPalette="brand"
                variant="outline"
                borderWidth="2px"
                fontWeight="bold"
                onClick={handleGetStarted}
                px={8}
              >
                Start Your Journey
              </Button>
            </Flex>
          </VStack>
        </Container>

        {/* Before & After Comparison */}
        <Box bg="teal.800" py={{ base: 16, md: 24 }} color="white" position="relative" overflow="hidden">
           {/* Background Accents */}
           <Box
            position="absolute"
            top="-50%"
            right="-20%"
            w="800px"
            h="800px"
            borderRadius="full"
            bgGradient="radial(brand.900, transparent)"
            opacity={0.3}
            filter="blur(100px)"
          />

          <Container maxW="7xl">
            <VStack gap={16}>
              <Box textAlign="center">
                <Heading size={{ base: '2xl', md: '3xl' }} mb={6} letterSpacing="tight">
                  Stop chasing clients. Start attracting them.
                </Heading>
                <Text fontSize="xl" color="gray.400" maxW="2xl" mx="auto">
                  See the difference between the "old way" and the Spotlight method.
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={12} w="full">
                {/* The Old Way */}
                <Box bg="whiteAlpha.100" p={8} borderRadius="2xl" border="1px solid" borderColor="whiteAlpha.100">
                  <HStack mb={6} color="red.300">
                    <Icon as={FiX} boxSize={6} />
                    <Heading size="md" color="white">Without Spotlight</Heading>
                  </HStack>
                  <VStack align="start" gap={4}>
                    <HStack align="start">
                      <Icon as={FiX} color="red.400" mt={1} />
                      <Text color="gray.300">Feeling invisible in a crowded market</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiX} color="red.400" mt={1} />
                      <Text color="gray.300">Frustrated by empty waiting lists despite your expertise</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiX} color="red.400" mt={1} />
                      <Text color="gray.300">Confused by Meta Ads & Google Ads managers</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiX} color="red.400" mt={1} />
                      <Text color="gray.300">Tired of spending hours on social media with no results</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiX} color="red.400" mt={1} />
                      <Text color="gray.300">Unpredictable income month-to-month</Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* The Spotlight Way */}
                <Box 
                  bg="brand.900" 
                  p={8} 
                  borderRadius="2xl" 
                  border="1px solid" 
                  borderColor="brand.500"
                  position="relative"
                  overflow="hidden"
                >
                  <Box position="absolute" top={0} right={0} p={4} opacity={0.1}>
                    <Icon as={FiCheck} boxSize={32} />
                  </Box>
                  <HStack mb={6} color="brand.300">
                    <Icon as={FiCheck} boxSize={6} />
                    <Heading size="md" color="white">With Spotlight</Heading>
                  </HStack>
                  <VStack align="start" gap={4}>
                    <HStack align="start">
                      <Icon as={FiCheck} color="brand.400" mt={1} />
                      <Text color="white" fontWeight="medium">A professional page done for you</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiCheck} color="brand.400" mt={1} />
                      <Text color="white" fontWeight="medium">Visibility on autopilot (Ads managed for you)</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiCheck} color="brand.400" mt={1} />
                      <Text color="white" fontWeight="medium">Clients discovering you every day</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiCheck} color="brand.400" mt={1} />
                      <Text color="white" fontWeight="medium">Simple analytics you actually understand</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiCheck} color="brand.400" mt={1} />
                      <Text color="white" fontWeight="medium">Pay only for visibility when you need it</Text>
                    </HStack>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Why transformation-focused service providers love this */}
        <Box bg="gray.50" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <VStack align="stretch" gap={16}>
              <Box textAlign="center">
                <Badge colorPalette="green" size="lg" mb={4} variant="subtle">
                  Benefits
                </Badge>
                <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" mb={4} letterSpacing="tight">
                  Everything you need, nothing you don't
                </Heading>
                <Text fontSize="xl" color="gray.600" maxW="2xl" mx="auto">
                  We stripped away the complexity so you can focus on your work.
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={12}>
                <VStack align="stretch" gap={8}>
                  {[
                    {
                      icon: FiZap,
                      title: "You never touch an ad manager. We handle everything.",
                      description: "We handle all the ad platforms, targeting, and optimization. You don't need to know what a 'pixel' or 'CTR' is."
                    },
                    {
                      icon: FiGlobe,
                      title: "A professional page that converts visitors into clients—we build it for you",
                      description: "Your Spotlight page acts as your professional landing page. It's designed to convert visitors into booked appointments."
                    },
                    {
                      icon: FiTarget,
                      title: "No marketing tech to learn. We protect you from wasted spend.",
                      description: "Skip the complex marketing tech stack. We provide the simple, direct path from 'stranger' to 'client'."
                    }
                  ].map((item, idx) => (
                    <HStack key={idx} align="start" gap={4} p={6} bg="white" borderRadius="xl" shadow="sm">
                      <Box p={3} bg="brand.50" borderRadius="lg" color="brand.600">
                        <Icon as={item.icon} boxSize={6} />
                      </Box>
                      <VStack align="start" gap={1}>
                        <Heading size="md" color="gray.900">{item.title}</Heading>
                        <Text color="gray.600">{item.description}</Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
                
                <VStack align="stretch" gap={8}>
                   {[
                    {
                      icon: FiTrendingUp,
                      title: "Instant Professional Presence",
                      description: "Launch with a polished, trustworthy profile that rivals established brands from day one."
                    },
                    {
                      icon: FiUsers,
                      title: "Immediate Visibility",
                      description: "Directories take months to rank. Spotlight ads put you in front of people immediately when you activate a pack."
                    },
                    {
                      icon: FiBarChart2,
                      title: "Crystal Clear Analytics",
                      description: "See exactly what you're getting: Impressions, Views, and Leads. No vanity metrics."
                    }
                  ].map((item, idx) => (
                    <HStack key={idx} align="start" gap={4} p={6} bg="white" borderRadius="xl" shadow="sm">
                      <Box p={3} bg="blue.50" borderRadius="lg" color="blue.600">
                        <Icon as={item.icon} boxSize={6} />
                      </Box>
                      <VStack align="start" gap={1}>
                        <Heading size="md" color="gray.900">{item.title}</Heading>
                        <Text color="gray.600">{item.description}</Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </SimpleGrid>

              <Flex justify="center">
                <Button 
                  size="xl" 
                  colorPalette="brand"
                  onClick={handleGetStarted}
                  px={12}
                  fontSize="lg"
                  fontWeight="bold"
                >
                  Start Getting Clients
                </Button>
              </Flex>
            </VStack>
          </Container>
        </Box>

        {/* What you get */}
        <Container maxW="7xl" py={{ base: 16, md: 24 }}>
          <VStack align="stretch" gap={16}>
            <Box textAlign="center">
              <Badge colorPalette="blue" size="lg" mb={4} variant="subtle">
                Deliverables
              </Badge>
              <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" mb={4} letterSpacing="tight">
                Your Complete Client-Acquisition System
              </Heading>
              <Text fontSize="xl" color="gray.600" maxW="2xl" mx="auto">
                Everything you need to get clients—no marketing expertise required.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
              {[
                {
                  icon: FiTarget,
                  title: "High-Converting Spotlight Page",
                  description: "A professionally designed landing page optimized to turn visitors into inquiries. Your page is free—you only pay for visibility."
                },
                {
                  icon: FiZap,
                  title: "Meta Carousel Ads (4-7 slides, generated instantly)",
                  description: "We generate carousel ads from your answers using story-first templates. Preview instantly, refine optionally. Included with every visibility pack."
                },
                {
                  icon: FiTrendingUp,
                  title: "Impression-Based Visibility Packs",
                  description: "Choose how many impressions you want. Pay only when you activate a pack. No subscriptions, no commitments."
                },
                {
                  icon: FiUsers,
                  title: "Lead Capture System",
                  description: "Integrated contact forms and call-to-actions that deliver leads straight to you."
                },
                {
                  icon: FiBarChart2,
                  title: "Simplified Dashboard",
                  description: "Track your growth with an analytics dashboard that speaks plain English."
                }
              ].map((item, idx) => (
                <VStack 
                  key={idx} 
                  align="start" 
                  gap={4} 
                  p={8} 
                  borderRadius="2xl" 
                  bg="white" 
                  border="1px solid" 
                  borderColor="gray.100"
                  shadow="sm"
                  _hover={{ shadow: 'lg', borderColor: 'brand.200', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  <Box p={3} bg="gray.50" borderRadius="xl" color="brand.600">
                    <Icon as={item.icon} boxSize={6} />
                  </Box>
                  <Box>
                    <Heading size="md" color="gray.900" mb={2} fontWeight="bold">
                      {item.title}
                    </Heading>
                    <Text fontSize="md" color="gray.600" lineHeight="tall">
                      {item.description}
                    </Text>
                  </Box>
                </VStack>
              ))}
            </SimpleGrid>

            <Flex justify="center">
              <Button
                size="lg"
                variant="outline"
                colorPalette="gray"
                onClick={handleGetStarted}
                fontWeight="semibold"
                px={8}
              >
                Start Getting Clients
              </Button>
            </Flex>
          </VStack>
        </Container>

        {/* Estimates Table */}
        <Box bg="gray.50" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <VStack align="stretch" gap={12}>
              <Box textAlign="center">
                <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" mb={4} letterSpacing="tight">
                  See what your visibility could look like
                </Heading>
                <Text fontSize="lg" color="gray.600" maxW="2xl" mx="auto">
                  Transparent estimates based on our ad performance data. Choose the impression pack that fits your goals.
                </Text>
              </Box>
              
              <Box bg="white" p={1} borderRadius="2xl" shadow="sm">
                <EstimatesTable />
              </Box>

              <Flex justify="center">
                <Button
                  size="xl"
                  colorPalette="brand"
                  onClick={() => navigate('/pricing')}
                  fontWeight="bold"
                  px={10}
                >
                  View Pricing & Packs
                </Button>
              </Flex>
            </VStack>
          </Container>
        </Box>

        {/* Trust Note */}
        <Box bg="gray.50" py={{ base: 8, md: 12 }}>
          <Container maxW="4xl">
            <VStack gap={4} textAlign="center">
              <HStack justify="center" gap={6} flexWrap="wrap">
                <HStack gap={2}>
                  <Icon as={FiCheck} color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold">Verified Pros:</Text> Providers verified for expertise and outcomes
                  </Text>
                </HStack>
                <HStack gap={2}>
                  <Icon as={FiCheck} color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold">Privacy-Safe:</Text> All metrics aggregated, no client names shared
                  </Text>
                </HStack>
                <HStack gap={2}>
                  <Icon as={FiCheck} color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold">Spotlight-Only:</Text> All ad traffic goes to Spotlight-managed landing pages
                  </Text>
                </HStack>
              </HStack>
            </VStack>
          </Container>
        </Box>

        {/* Bottom CTA */}
        <Box bg="teal.700" color="white" py={{ base: 20, md: 32 }} position="relative" overflow="hidden">
           <Box
            position="absolute"
            top="0"
            left="0"
            w="full"
            h="full"
            bgGradient="linear(to-br, teal.700, teal.800)"
            opacity={0.9}
            zIndex={0}
          />
          <Container maxW="4xl" position="relative" zIndex={1}>
            <VStack gap={8} textAlign="center">
              <Heading size={{ base: '3xl', md: '4xl' }} letterSpacing="tight">
                Ready to fill your calendar?
              </Heading>
              <Text fontSize="xl" color="teal.100" maxW="2xl" lineHeight="tall">
                Join the professionals who have stopped worrying about marketing and started focusing on their work.
              </Text>
              <VStack gap={4} w="full">
                <Button
                  size="xl"
                  bg="white"
                  color="teal.700"
                  fontWeight="bold"
                  px={12}
                  py={8}
                  fontSize="xl"
                  onClick={handleGetStarted}
                  _hover={{ transform: 'scale(1.05)', shadow: '2xl', bg: 'gray.100' }}
                  transition="all 0.2s"
                >
                  <HStack gap={3}>
                    <Text>Start Getting Clients</Text>
                    <Icon as={FiArrowRight} />
                  </HStack>
                </Button>
                <Text fontSize="sm" color="gray.500">
                  No subscriptions. Pay only for visibility packs when you're ready.
                </Text>
              </VStack>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box 
        position="fixed" 
        bottom={0} 
        left={0} 
        right={0} 
        bg="white" 
        p={4} 
        borderTopWidth="1px" 
        borderColor="gray.200"
        shadow="xl" 
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button 
          w="full" 
          colorPalette="brand" 
          size="lg" 
          onClick={handleGetStarted}
          fontWeight="bold"
        >
          <HStack gap={2}>
            <Text>Start Getting Clients</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}

export default ForPractitionersPage
